import { setValueAtPath } from 'utils/valueFromObject';

/**
 * Recursive function to determine the differences between two objects and their paths in a dictionary structure where
 * the keys are the path and the values are the boolean true. This mimics what dirtyFields looks like in react-hook-form.
 *
 * There is an additional assumption that all keys in defaultValues will occur in currentValues, therefore it is not
 * possible for a key to exist in defaultValues and not in currentValues.
 * @param currentValues the current values of field names
 * @param defaultValues the default values of field names
 * @param currentPath the 'root', i.e., if the root is different, what should we use as the base?
 */
function getDirtyFieldsInternal(
  currentValues: any,
  defaultValues: any,
  currentPath: string
): Record<string, boolean> {
  let result: Record<string, boolean> = {};

  // if they're the same object, return empty
  if (currentValues === defaultValues) {
    return result;
  }

  // if they're both arrays
  if (Array.isArray(currentValues) && Array.isArray(defaultValues)) {
    // if they have different lengths, the current path is changed, so mark it and return
    if (currentValues.length !== defaultValues.length) {
      result[currentPath] = true;
      return result;
    }

    // loop over each element in the first array, and check if it is the same as in the second array
    currentValues.forEach((elem, index) => {
      const dirtyFields = getDirtyFieldsInternal(
        elem,
        defaultValues[index],
        `${currentPath}[${index}]`
      );
      // append differences in array
      if (Object.keys(dirtyFields).length > 0) {
        result = { ...result, ...dirtyFields };
      }
    });

    return result;
  }

  // if both are objects
  if (
    typeof currentValues === 'object' &&
    typeof defaultValues === 'object' &&
    currentValues !== null &&
    defaultValues !== null
  ) {
    // if either is an array
    if (Array.isArray(currentValues) || Array.isArray(defaultValues)) {
      result[currentPath] = true;
      return result;
    }

    const keys2 = Object.keys(defaultValues);

    // loop over all keys in currentValues
    Object.keys(currentValues).forEach((key): void => {
      // check if defaultValues has the same key, and its value is not undefined
      if (!keys2.includes(key) && currentValues[key] !== undefined) {
        setValueAtPath(
          result,
          currentPath === '' ? key : `${currentPath}.${key}`,
          true
        );
        // result[currentPath === '' ? key : `${currentPath}.${key}`] = true;
        return; // potential source of error because we return nothing effectively
      }
      const dirtyFields = getDirtyFieldsInternal(
        currentValues[key],
        defaultValues[key],
        currentPath === '' ? key : `${currentPath}.${key}`
      );
      if (Object.keys(dirtyFields).length > 0) {
        result = { ...result, ...dirtyFields };
      }
    });
    return result;
  }

  // if we don't early return, then this path must be different, so append it
  setValueAtPath(result, currentPath, true);
  // result[currentPath] = true;
  return result;
}

/**
 * Since dirty fields is unreliable, we perform our own check, using '' as a root
 * @param currentValues current values of field names
 * @param defaultValues default values of field names
 */
export function getDirtyFields(
  currentValues: any,
  defaultValues: any
): Record<string, boolean> {
  return getDirtyFieldsInternal(currentValues, defaultValues, '');
}

export function areDeepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    if (
      !Array.isArray(obj1) ||
      !Array.isArray(obj2) ||
      obj1.length !== obj2.length
    )
      return false;

    return obj1.every((elem, index) => areDeepEqual(elem, obj2[index]));
  }

  if (
    typeof obj1 === 'object' &&
    typeof obj2 === 'object' &&
    obj1 !== null &&
    obj2 !== null
  ) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (
      keys1.length !== keys2.length ||
      !keys1.every((key) => keys2.includes(key))
    )
      return false;

    for (let key = 0; obj1.length; key++) {
      if (!areDeepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return Object.keys(obj1).every((key) => areDeepEqual(obj1[key], obj2[key]));
  }

  return false;
}
